import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import AuthLayout from "@/components/auth/AuthLayout";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/lib/supabaseClient";
import { PENDING_JOIN_SIGNUP_KEY } from "@/pages/JoinClinic";

function readUrlState() {
  const params = new URLSearchParams(window.location.search);
  const hash = new URLSearchParams((window.location.hash || "").replace(/^#/, ""));
  return {
    intent: params.get("intent") || hash.get("intent") || "",
    code: params.get("code") || hash.get("code") || "",
    error: params.get("error") || hash.get("error") || "",
    errorCode: params.get("error_code") || hash.get("error_code") || "",
    errorDescription: params.get("error_description") || hash.get("error_description") || "",
  };
}

function readPendingJoin() {
  try {
    return JSON.parse(window.localStorage.getItem(PENDING_JOIN_SIGNUP_KEY) || "null");
  } catch {
    return null;
  }
}

export default function AuthConfirmed() {
  const navigate = useNavigate();
  const { session, loading, refreshClinics } = useAuth();
  const urlState = useMemo(() => readUrlState(), []);
  const [status, setStatus] = useState("checking");
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (urlState.error || urlState.errorCode) {
      setStatus("error");
      setMessage(urlState.errorDescription || "That email link is expired, invalid, or was already used.");
      return;
    }
    if (loading) return;
    if (!session) {
      setStatus("error");
      setMessage("That email link could not start a verified session. It may be expired or already used.");
      return;
    }

    let cancelled = false;
    (async () => {
      if (urlState.intent === "join") {
        const pending = readPendingJoin();
        const code = urlState.code || pending?.code;
        if (!code) {
          navigate("/dashboard", { replace: true });
          return;
        }
        setStatus("joining");
        const { data, error } = await supabase.rpc("accept_invitation", {
          p_code: code,
          p_full_name: pending?.fullName || null,
          p_display_name: pending?.displayName || null,
          p_prc_license: pending?.prcLicense || null,
        });
        if (cancelled) return;
        if (error || data?.ok === false) {
          setStatus("error");
          setMessage(error?.message || data?.message || "That invitation could not be accepted.");
          return;
        }
        await supabase.auth.refreshSession();
        await refreshClinics();
        window.sessionStorage.removeItem("dental_os_pending_invite_code");
        window.localStorage.removeItem(PENDING_JOIN_SIGNUP_KEY);
        setStatus("done");
        navigate("/dashboard", { replace: true });
        return;
      }

      setStatus("done");
      navigate("/signup/clinic", { replace: true });
    })();
    return () => { cancelled = true; };
  }, [loading, navigate, refreshClinics, session, urlState]);

  if (status === "error") {
    return (
      <AuthLayout
        title="Email link problem"
        subtitle="This verification link cannot be used."
        footer={<Link to="/login" className="text-teal-700 font-medium hover:underline">Back to sign in</Link>}
      >
        <div data-testid="auth-confirmed-error" className="flex items-start gap-3 rounded-lg border border-rose-100 bg-rose-50 p-4 text-rose-700">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
          <p className="text-sm leading-6">{message}</p>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={status === "joining" ? "Joining clinic" : "Verifying email"}
      subtitle="One moment while Dental OS finishes your secure sign-in."
    >
      <div data-testid="auth-confirmed-loading" className="flex items-center gap-3 text-sm text-slate-600">
        {status === "done" ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : <Loader2 className="h-5 w-5 animate-spin text-teal-600" />}
        <span>{status === "joining" ? "Adding you to the clinic..." : "Checking your verified session..."}</span>
      </div>
    </AuthLayout>
  );
}
